/**
 * Право показать предложение установить приложение — отдельным чистым модулем, а не
 * условием внутри компонента.
 *
 * Причина в том, что ломается здесь молча. «Не iPad», «не внутри самого приложения»,
 * «не раньше чем через месяц после отказа» — правила, у которых нет ни одного видимого
 * симптома при поломке: баннер либо не покажется тому, кому был нужен, либо покажется
 * тому, кто уже установил приложение. Ни то, ни другое не даёт ошибки нигде.
 *
 * Подход перенесён из соседнего проекта кампуса (`../Campus/CampusImage/web/src/lib/ios-app.ts`).
 * Кода общего с ним нет и не будет — взяты только правила отбора и их обоснования.
 */

/** Ответ «не сейчас» помнится МЕЖДУ сессиями: спрашивать на каждой вкладке — навязчиво. */
export const IOS_DISMISS_KEY = 'mm.ios-app.dismissed';

/** …но не навсегда: за месяц человек мог передумать, а приложение — измениться. */
export const IOS_DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Маркер, который приложение MathMind обязано добавлять в User-Agent, если однажды станет
 * показывать страницы сайта своим веб-вью. Без него сайт, открытый ВНУТРИ приложения,
 * предлагал бы установить приложение, в котором он уже открыт.
 *
 * ⚠️ Строку нужно согласовать с iOS-репозиторием (`../MathMindImageIOS`). Проверка стоит
 * до согласования сознательно: она безвредна на любом другом агенте и включится сама,
 * как только клиент начнёт слать маркер. Обратный порядок означал бы, что о ней вспомнят
 * тогда, когда пользователи уже увидят баннер внутри приложения.
 */
const IN_APP_MARKER = 'MathMindApp';

/** Снимок среды: всё перечисленное в пределах вкладки не меняется. */
export interface IosProbe {
  readonly userAgent: string;
  readonly platform: string;
  /** Safari: сайт добавлен на экран «Домой» и запущен оттуда. */
  readonly standalone: boolean;
  /** То же самое для остальных движков — через `(display-mode: standalone)`. */
  readonly displayModeStandalone: boolean;
}

/**
 * Именно iPhone, а не «любой iOS».
 *
 * iPad исключён сознательно: поддерживает ли приложение планшет, из этого репозитория
 * не проверить, а нативный Smart App Banner решает вопрос совместимости сам — владельцу
 * iPad предложение всё равно достанется, если оно уместно.
 *
 * ⚠️ Отбор идёт по признаку «есть iPhone/iPod», а НЕ по «это не Mac»: строка айфона
 * содержит `like Mac OS X`, и проверка на Mac, поставленная раньше, отсекала бы сам
 * iPhone. По той же причине здесь не нужен `maxTouchPoints`, которым обычно ловят
 * iPadOS 13+: тот представляется Mac-ом, слова iPhone в его строке нет — он отсеивается сам.
 */
export function isIphone(probe: IosProbe): boolean {
  if (/iPad/u.test(probe.platform) || /iPad/u.test(probe.userAgent)) return false;
  return /iPhone|iPod/u.test(probe.platform) || /iPhone|iPod/u.test(probe.userAgent);
}

/**
 * Момент, до которого предложение скрыто. `0` — не отказывались (или память недоступна).
 *
 * ⚠️ Мусор в ключе (чужая версия, ручная правка) читается как «отказа не было»: так
 * значение само чинится следующим отказом, а не прячет предложение навсегда.
 *
 * `key` — чтобы тем же правилом пользовался Android-баннер (lib/android-app.ts): отказы
 * помнятся раздельно, но читаются и пишутся одинаково.
 */
export function dismissedUntil(
  storage: Pick<Storage, 'getItem'> | null,
  key: string = IOS_DISMISS_KEY,
): number {
  try {
    const raw = storage?.getItem(key) ?? null;
    if (raw === null) return 0;
    const at = Number.parseInt(raw, 10);
    return Number.isFinite(at) ? at + IOS_DISMISS_TTL_MS : 0;
  } catch {
    // Приватный режим Safari бросает на самом обращении к хранилищу. Это не ошибка.
    return 0;
  }
}

export function rememberDismiss(
  storage: Pick<Storage, 'setItem'> | null,
  now: number,
  key: string = IOS_DISMISS_KEY,
): void {
  try {
    storage?.setItem(key, String(now));
  } catch {
    // Не запомнили — предложим снова в следующей вкладке. Тоже не ошибка.
  }
}

export interface IosOfferInput {
  /** Адрес карточки в App Store из манифеста. Пусто = приложения у деплоя нет. */
  readonly storeUrl: string;
  readonly probe: IosProbe;
  readonly storage: Pick<Storage, 'getItem'> | null;
  readonly now: number;
}

export function shouldOfferIosApp(input: IosOfferInput): boolean {
  if (input.storeUrl === '') return false;
  if (input.probe.userAgent.includes(IN_APP_MARKER)) return false;
  if (input.probe.standalone || input.probe.displayModeStandalone) return false;
  if (!isIphone(input.probe)) return false;
  return input.now >= dismissedUntil(input.storage);
}

/** Снимок среды один раз на монтирование: в пределах вкладки ничего из этого не меняется. */
export function readIosProbe(): IosProbe {
  const nav = navigator as Navigator & { standalone?: boolean };
  let displayModeStandalone = false;
  try {
    displayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    // Движок без matchMedia — считаем, что это обычная вкладка.
  }
  return {
    userAgent: nav.userAgent,
    /**
     * `platform` объявлен устаревшим, но поддержан всей матрицей и здесь только уточняет
     * ответ: решение принимается и по строке агента, так что его исчезновение не сломает
     * отбор, а лишь оставит один признак вместо двух.
     */
    platform: nav.platform,
    standalone: nav.standalone === true,
    displayModeStandalone,
  };
}

export function readIosStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // В приватном режиме Safari бросает уже на обращении к свойству.
    return null;
  }
}
